import React, { useState, useEffect, useRef } from "react";

// Browser-native text-to-speech (Web Speech API): no API key, no per-character cost.
// ponytail: system voices vary by OS/browser; a paid ElevenLabs plan behind the backend is the upgrade path.
const LANG_CODES = { en: "en-US", sr: "sr-RS", ru: "ru-RU", ar: "ar-SA" };

const synth = typeof window !== "undefined" ? window.speechSynthesis : null;

// Strip markdown/code noise so it isn't read out loud
const cleanTextForSpeech = (text) =>
  (text || "")
    .replace(/```[\s\S]*?```/g, " code block ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/<[^>]*>/g, "")
    .replace(/[#*_>|~{}[\]\\]/g, " ")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s+/g, " ")
    .trim();

const pickVoice = (lang) => {
  const voices = synth.getVoices();
  const base = lang.split("-")[0];
  return voices.find((v) => v.lang === lang) || voices.find((v) => v.lang.startsWith(base)) || null;
};

const TextToSpeech = ({ text, isDarkMode, language = "en" }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const remaining = useRef(0);

  // Stop if this message unmounts mid-speech
  useEffect(() => () => remaining.current > 0 && synth.cancel(), []);

  if (!synth) return null;

  const speak = () => {
    if (isSpeaking) {
      synth.cancel();
      setIsSpeaking(false);
      return;
    }
    synth.cancel(); // stop any other message that is playing

    const lang = LANG_CODES[language] || LANG_CODES.en;
    const voice = pickVoice(lang);
    // One utterance per sentence: Chrome silently stops long utterances after ~15s
    const sentences = cleanTextForSpeech(text).match(/[^.!?]+[.!?]*/g) || [];
    if (!sentences.length) return;

    remaining.current = sentences.length;
    sentences.forEach((sentence) => {
      const u = new SpeechSynthesisUtterance(sentence.trim());
      u.lang = lang;
      if (voice) u.voice = voice;
      u.onend = u.onerror = () => {
        remaining.current -= 1;
        if (remaining.current <= 0) setIsSpeaking(false);
      };
      synth.speak(u);
    });
    setIsSpeaking(true);
  };

  return (
    <button
      onClick={speak}
      className={`px-3 py-2 rounded-lg border flex items-center gap-2 transition-all duration-200 ${
        isSpeaking
          ? isDarkMode
            ? "border-blue-600 bg-blue-500/20 text-blue-400"
            : "border-blue-600 bg-blue-100 text-blue-600"
          : isDarkMode
          ? "border-gray-700 hover:border-gray-600 text-gray-400 hover:text-gray-300 hover:bg-gray-800/50"
          : "border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
      }`}
      title={isSpeaking ? "Stop speaking" : "Read this reply aloud"}
    >
      <i className={`fas ${isSpeaking ? "fa-stop-circle" : "fa-volume-up"}`}></i>
      <span className="text-sm">{isSpeaking ? "Stop" : "Listen"}</span>
    </button>
  );
};

export default TextToSpeech;
